import type { LibraryPrompt } from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * The prompt library.
 */

export async function fetchPromptLibrary(workspaceId: string): Promise<{
  /*
   * The contract's own row, not a hand-written subset. It listed four fields
   * and the endpoint returns seven, so a screen asking for the reports reading
   * a question was told the field did not exist — while the response carried it
   * all along.
   */
  prompts: readonly LibraryPrompt[];
  used: number;
  quota: number;
}> {
  const response = await api.GET("/api/workspaces/{workspaceId}/prompts", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load your questions"));
  }
  return response.data;
}

export type PromptLibrary = Awaited<ReturnType<typeof fetchPromptLibrary>>;

export async function createPrompt(
  workspaceId: string,
  body: { text: string; tags?: string[] },
): Promise<{ id: string; text: string; scheduled: number }> {
  const response = await api.POST("/api/workspaces/{workspaceId}/prompts", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    const status = response.response.status;
    if (status === 429) {
      throw new Error(
        "Your plan has no room for another question. Remove one, or raise the quota.",
      );
    }
    if (status === 409) {
      throw new Error("That question is already in your library for this country.");
    }
    if (status === 400) {
      /*
       * The server's own sentence, which names what is wrong with this
       * particular question. The line here used to be invented — "see the notes
       * above it" — and there were no notes above it, so the one screen that
       * knew nothing sent the customer looking for the one thing that did.
       */
      const said = response.error.message;
      throw new Error(said === "" ? "That question cannot measure anything yet." : said);
    }
    throw new Error(problem(status, "save that question"));
  }
  return response.data;
}

/**
 * Remove questions from the library, and every capture made for them.
 *
 * Answers with the number of rows the workspace actually held rather than with
 * a success of its own, which is what makes a stale selection legible: two tabs
 * deleting the same question delete it once, and the second call says so.
 */
export async function deletePrompts(
  workspaceId: string,
  promptIds: readonly string[],
): Promise<number> {
  const response = await api.DELETE("/api/workspaces/{workspaceId}/prompts", {
    params: { path: { workspaceId } },
    body: { promptIds: [...promptIds] },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "delete those questions"));
  }
  return response.data.deleted;
}
